import React, { useCallback, useRef, useEffect } from 'react';
import styled, { withTheme } from 'styled-components';
import PropTypes from 'prop-types';
import { withValue } from '@g4/automation-hooks';
import tracking from '@g4/g4-analytics';

import Cart from '../../components/siteLayout/siteHeader/Cart';
import { Box, Text, useMedia, Expando } from '../../components/Boneyard';

import { useBookingPath, useCart, useSeatSizesMap } from '../../graphql';
import { useUiCart } from '../../graphql/localState/uiCart.hooks';
import useBookingSteps from '../../hooks/useBookingSteps';
import useFlightSearchCriteria from '../../hooks/useFlightSearchCriteria';
import { preventPropagationProps } from '../../hooks/useOnClickOutside';
import { useTranslations } from '../../graphql/components/TranslationsProvider';
import { getCartRawProducts } from '../app/trackingItems';

import ARROW_ICON from '../../images/ic-arrow-blue.svg';

const propTypes = {
  theme: PropTypes.object,
  total: PropTypes.number,
  automationHook: PropTypes.string,
};

export const TripSummaryButton = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-height: ${props => props.maxHeight};
`;

export const ArrowIconContainer = styled.div`
  display: flex;
  margin-left: ${props => props.theme.spacing(1)};

  transform: ${props =>
    props.isOpen ? 'rotate(0deg) translateY(0)' : 'rotate(180deg)'};
  transition: 0.2s;
`;

export const ExpandoLabel = styled.div`
  width: 100%;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-transform: uppercase;
  background-color: ${props =>
    props.theme.colors.payment.tripSummaryBackground};
  border-top-right-radius: ${props => (props.isFooter ? '0px' : '8px')};
  border-top-left-radius: ${props => (props.isFooter ? '0px' : '8px')};
  border-bottom-right-radius: ${props => (props.isOpen ? '0px' : '8px')};
  border-bottom-left-radius: ${props => (props.isOpen ? '0px' : '8px')};
  padding: ${props => props.theme.spacing(4)} ${props => props.theme.spacing(1)}
    ${props => props.theme.spacing(4)} ${props => props.theme.spacing(4)};
  border-left: ${props =>
    props.isFooter ? 'none' : `2px solid ${props.theme.colors.payment.border}`};
  border-top: 2px solid ${props => props.theme.colors.payment.border};
  border-right: ${props =>
    props.isFooter ? 'none' : `2px solid ${props.theme.colors.payment.border}`};
  border-bottom: ${props =>
    props.isOpen ? 'none' : `2px solid ${props.theme.colors.payment.border}`};
`;

export const ArrowIcon = styled.img.attrs({
  src: ARROW_ICON,
  alt: '',
})`
  width: 20px;
  height: 20px;
`;

const TripSummary = ({ total, automationHook, theme }) => {
  const mq = useMedia();
  const tripSummaryRef = useRef();
  const { isChangingRoute } = useBookingSteps();
  const { operatedBy } = useBookingPath();
  const flightSearchCriteria = useFlightSearchCriteria();

  const [{ isOpen: isTripSummaryOpen }, updateUiCart] = useUiCart();
  const { seatSizesMap } = useSeatSizesMap();

  useEffect(() => {
    if (isChangingRoute) {
      return;
    }
    tripSummaryRef.current?.scrollIntoView(false);
  }, [isChangingRoute, isTripSummaryOpen]);

  const { tripSummaryTitle } = useTranslations()?.paymentPage || {};

  const setIsTripSummaryExpanded = useCallback(
    isOpen => {
      updateUiCart({ isOpen });
    },
    [updateUiCart]
  );
  const toggleIsExpanded = () => {
    if (!isTripSummaryOpen) {
      const cartItems = getCartRawProducts(
        cart,
        operatedBy,
        flightSearchCriteria?.data || {},
        seatSizesMap
      );

      //Trigger event as multiple hits when payload exceeds maximum limit
      cartItems.forEach((rawProduct, index) => {
        const trackData = {
          currency: 'USD',
          value: cart?.order?.price?.total,
          products: rawProduct,
        };
        setTimeout(() => {
          tracking.trackCartClick(trackData);
        }, index * 1000);
      });
    }

    setIsTripSummaryExpanded(!isTripSummaryOpen);
    tracking.trackEvent(
      'site_click_tracking',
      'Review and payment',
      isTripSummaryOpen ? 'Collapse Trip Summary' : 'Expand Trip Summary'
    );
  };

  const { cart, cartError, cartLoading } = useCart();

  if (cartError) {
    throw cartError;
  }
  return (
    <Expando
      open={isTripSummaryOpen}
      onToggle={toggleIsExpanded}
      a11yLabel={'Trip summary section'}
      automationHook={withValue(automationHook, 'trip_summary_header')}
      icon="NONE"
      label={
        <ExpandoLabel isOpen={isTripSummaryOpen}>
          <TripSummaryButton {...preventPropagationProps()}>
            <Text size={mq.gt('sm') ? 24 : 17} weight={500} uppercase={true}>
              {tripSummaryTitle}
            </Text>
            <Text
              size={mq.gt('sm') ? 24 : 17}
              uppercase={true}
              automationHook={withValue(automationHook, 'trip_summary_amount')}
            >
              ${total.toFixed(2)}
            </Text>
          </TripSummaryButton>
          <ArrowIconContainer isOpen={isTripSummaryOpen}>
            <ArrowIcon ref={tripSummaryRef} />
          </ArrowIconContainer>
        </ExpandoLabel>
      }
    >
      <Box
        b={`2px solid ${theme.colors.payment.border}`}
        automationHook={withValue(
          automationHook,
          'trip_summary_cart_container'
        )}
        borderRadius={mq.gt('sm') ? 0 : [0, 0, 8, 8]}
      >
        {cartLoading ? (
          <></>
        ) : (
          <Cart
            isTripSummary
            data={cart}
            automationHook={withValue(automationHook, 'trip_summary_content')}
          />
        )}
      </Box>
    </Expando>
  );
};
TripSummary.propTypes = propTypes;

export default withTheme(TripSummary);
